import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/Feedback'

/** What deploy/linux/publish-download.sh writes beside each application's files. */
export type DownloadManifest = {
  application: string
  version: string
  file: string
  bytes: number
  sha256: string
  publishedAt: string
}

/** One published file each: the Windows installer, the Linux package and the Android APK. */
type DownloadApp = 'escritorio' | 'escritorio-linux' | 'movil'
type Published = { state: 'loading' } | { state: 'ready'; manifest: DownloadManifest } | { state: 'unpublished' } | { state: 'failed' }

const DOWNLOADS = '/descargas/'

/** The version on offer, or null when none is published: nginx answers 404, and a development server its own page. */
async function readManifest(app: DownloadApp): Promise<DownloadManifest | null> {
  const response = await fetch(`${DOWNLOADS}${app}.json`, { cache: 'no-cache' })
  if (response.status === 404 || !(response.headers.get('content-type') ?? '').includes('json')) return null
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const manifest = (await response.json()) as DownloadManifest
  if (typeof manifest.version !== 'string' || typeof manifest.file !== 'string' || typeof manifest.bytes !== 'number') throw new Error('Manifiesto incompleto')
  return manifest
}

function downloadUrl(manifest: DownloadManifest): string {
  return new URL(`${DOWNLOADS}${manifest.file}`, window.location.origin).href
}

/** The name the browser saves the download as. */
function fileName(manifest: DownloadManifest): string {
  return manifest.file.split('/').pop() ?? manifest.file
}

function formatSize(bytes: number): string {
  return `${(bytes / 1_048_576).toLocaleString('es-MX', { maximumFractionDigits: 1 })} MB`
}

function usePublished(app: DownloadApp): [Published, () => void] {
  const [published, setPublished] = useState<Published>({ state: 'loading' })
  const load = useCallback(() => {
    setPublished({ state: 'loading' })
    readManifest(app)
      .then((manifest) => setPublished(manifest ? { state: 'ready', manifest } : { state: 'unpublished' }))
      .catch(() => setPublished({ state: 'failed' }))
  }, [app])
  useEffect(() => { load() }, [load])
  return [published, load]
}

function Download({ name, platform, action, hint, published, retry }: { name: string; platform: string; action: string; hint?: (manifest: DownloadManifest) => ReactNode; published: Published; retry: () => void }) {
  const detail = published.state === 'ready'
    ? `${platform} · versión ${published.manifest.version} · ${formatSize(published.manifest.bytes)}`
    : platform
  return <div className="download-row">
    <div>
      <strong>{name}</strong>
      <span>{detail}</span>
      {published.state === 'ready' && hint && <p className="download-hint">{hint(published.manifest)}</p>}
      {published.state === 'ready' && <p className="download-hash">SHA-256 {published.manifest.sha256}</p>}
    </div>
    {published.state === 'ready' && <a className="ui-button" data-intent="primary" data-emphasis="solid" href={downloadUrl(published.manifest)} download>{action}</a>}
    {published.state === 'loading' && <span className="download-status" role="status">Buscando la versión publicada…</span>}
    {published.state === 'unpublished' && <span className="download-status">Todavía no hay una versión publicada.</span>}
    {published.state === 'failed' && <Button type="button" emphasis="outline" intent="neutral" onClick={retry}>Reintentar</Button>}
  </div>
}

/** The installers a company's computers and phones run, the same files for every company. */
export function DownloadsPanel() {
  const [windows, retryWindows] = usePublished('escritorio')
  const [linux, retryLinux] = usePublished('escritorio-linux')
  const [mobile, retryMobile] = usePublished('movil')
  const desktopFailed = windows.state === 'failed' || linux.state === 'failed'
  return <>
    <section className="subscription-panel downloads-panel" aria-labelledby="desktop-downloads-title">
      <h2 id="desktop-downloads-title">Software de escritorio</h2>
      <p>Instala BsPOS en cada computadora y después incorpórala con un código de la sección Incorporar terminal. El mismo archivo sirve para todas tus terminales.</p>
      {desktopFailed && <Notice>No pudimos consultar la versión publicada. Revisa tu conexión y vuelve a intentarlo.</Notice>}
      <Download name="Windows" platform="Windows 10 u 11 de 64 bits" action="Descargar para Windows" published={windows} retry={retryWindows} />
      <Download name="Linux" platform="Ubuntu 22.04 o más nuevo, 64 bits" action="Descargar para Linux" hint={(manifest) => <>Al terminar la descarga, instálalo desde una terminal en la carpeta de descargas; Ubuntu pedirá la contraseña del equipo: <code>sudo apt install ./{fileName(manifest)}</code></>} published={linux} retry={retryLinux} />
    </section>
    <section className="subscription-panel downloads-panel" aria-labelledby="mobile-downloads-title">
      <h2 id="mobile-downloads-title">Software móvil</h2>
      <p>BsPOS móvil para los teléfonos de la tienda; se incorpora con un código igual que una terminal.</p>
      {mobile.state === 'failed' && <Notice>No pudimos consultar la versión publicada. Revisa tu conexión y vuelve a intentarlo.</Notice>}
      <Download name="Android" platform="Android" action="Descargar APK" published={mobile} retry={retryMobile} />
      {mobile.state === 'ready' && <div className="mfa-registration">
        <div className="mfa-qr" aria-hidden="true"><QRCodeSVG value={downloadUrl(mobile.manifest)} level="M" includeMargin /></div>
        <div className="mfa-manual-key">
          <p>Escanéalo con la cámara del teléfono para descargar BsPOS móvil ahí mismo.</p>
          <p className="field-hint">Android te pedirá permitir que el navegador instale aplicaciones; acéptalo solo para este archivo.</p>
        </div>
      </div>}
    </section>
  </>
}
